import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Eye, EyeOff, GripVertical, Lock, Plus, RotateCcw, Trash2 } from "lucide-react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  cn,
} from "@nexus/ui";
import { PINNED_KPI_NAME, KPI_DECIMALS_MAX, type KpiCatalogEntry, type KpiCatalogFormat, type KpiPolarity, type KpiColorToken } from "@nexus/contracts";
import { kpiCatalogKeys, tenantKpisQueryOptions } from "@/lib/queries";
import { createTenantKpi, deleteTenantKpi, reorderTenantKpis, updateTenantKpi } from "@/features/identity-hub/api/tenant-kpis.api";
import { useIamPermissionForTenant } from "@/features/auth/hooks/usePermissions";
import { getUserFacingApiMessage } from "@/lib/api-client";
import { toast } from "sonner";
import { KpiColorPicker } from "@/features/kpi-settings/components/KpiColorPicker";
import {
  KpiSettingsDialog,
  type KpiSettingsDialogValue,
} from "@/features/kpi-settings/components/KpiSettingsDialog";
import { asKpiColorToken, resolveCatalogColorVar } from "@/features/kpi-settings/lib/kpi-color";
import { clampKpiDecimals } from "@/features/kpi-settings/lib/kpi-decimals";
import { OrgDefaultNorthStarControl } from "./OrgDefaultNorthStarControl";

const FORMAT_OPTIONS: KpiCatalogFormat[] = ["CURRENCY", "PERCENT", "NUMBER"];
const POLARITY_OPTIONS: KpiPolarity[] = ["NEGATIVE", "POSITIVE"];

const KPI_GRID_CLASS =
  "grid grid-cols-[20px_20px_minmax(0,1fr)_104px_168px_60px_84px_28px] items-center gap-2.5 px-3";
// Row padding + grip column + color column + two gaps, so notes line up under the name input.
const KPI_NOTE_INDENT_CLASS = "pl-[62px]";
const KPI_HEADER_LABEL_CLASS = "text-[10px] uppercase leading-none tracking-[0.4px] text-muted-foreground";
const KPI_CONTROL_CLASS =
  "h-8 w-full min-w-0 rounded-md border border-border bg-background text-xs text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50";

interface OrgKpiSettingsTabProps {
  tenantId: string;
  locked: boolean;
}

export function OrgKpiSettingsTab({ tenantId, locked }: OrgKpiSettingsTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canManage = useIamPermissionForTenant(tenantId, "IDENTITY_HUB_UPDATE_TENANT");
  const canViewCatalog =
    useIamPermissionForTenant(tenantId, "IDENTITY_HUB_VIEW_PAGE") || canManage;
  const readOnly = locked || !canManage;

  const query = useQuery({
    ...tenantKpisQueryOptions(tenantId),
    enabled: canViewCatalog && !!tenantId,
  });
  const rows = query.data ?? [];
  const [createOpen, setCreateOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sortableIds = useMemo(
    () => rows.filter((row) => row.name !== PINNED_KPI_NAME).map((row) => String(row.id)),
    [rows],
  );

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: kpiCatalogKeys.all });
    void queryClient.invalidateQueries({ queryKey: kpiCatalogKeys.tenant(tenantId) });
  }

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof updateTenantKpi>[2] }) =>
      updateTenantKpi(tenantId, id, body),
    onSuccess: invalidate,
    onError: (error) => toast.error(getUserFacingApiMessage(error)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteTenantKpi(tenantId, id),
    onSuccess: invalidate,
    onError: (error) => toast.error(getUserFacingApiMessage(error)),
  });

  const reorderMut = useMutation({
    mutationFn: (names: string[]) => reorderTenantKpis(tenantId, names),
    onSuccess: invalidate,
    onError: (error) => toast.error(getUserFacingApiMessage(error)),
  });

  const createMut = useMutation({
    mutationFn: (body: Parameters<typeof createTenantKpi>[1]) => createTenantKpi(tenantId, body),
    onSuccess: () => {
      invalidate();
      setCreateOpen(false);
    },
    onError: (error) => toast.error(getUserFacingApiMessage(error)),
  });

  function handleDragEnd(event: DragEndEvent) {
    if (readOnly) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const unpinned = rows.filter((row) => row.name !== PINNED_KPI_NAME);
    const oldIndex = unpinned.findIndex((row) => String(row.id) === String(active.id));
    const newIndex = unpinned.findIndex((row) => String(row.id) === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const nextUnpinned = arrayMove(unpinned, oldIndex, newIndex);
    const pinned = rows.find((row) => row.name === PINNED_KPI_NAME);
    const names = [
      ...(pinned ? [pinned.name] : []),
      ...nextUnpinned.map((row) => row.name),
    ];
    reorderMut.mutate(names);
  }

  if (!canViewCatalog) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("iam.orgSettings.kpis.noPermission", "You do not have permission to view KPIs for this organization.")}
      </p>
    );
  }

  if (query.isLoading) {
    return <div className="h-32 rounded-md border border-border bg-muted/40 animate-pulse" />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="border-b border-border-subtle pb-5">
        <OrgDefaultNorthStarControl tenantId={tenantId} disabled={readOnly} />
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="flex max-w-[560px] flex-col gap-0.5">
          <p className="text-[13px] font-semibold leading-snug text-foreground">
            {t("iam.orgSettings.kpis.title", "KPI catalog")}
          </p>
          <p className="text-xs leading-normal text-muted-foreground">
            {t(
              "iam.orgSettings.kpis.hint",
              "Hide a KPI to remove it from Overview, Drilldown and Topic Detail. MCP still sees the full catalog. Spend stays first. Drag to reorder.",
            )}
          </p>
        </div>
        {canManage && !locked && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 shrink-0 gap-1.5"
            data-testid="org-settings-kpi-create"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            {t("iam.orgSettings.kpis.add", "Add KPI")}
          </Button>
        )}
      </div>

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border">
        <div className={cn(KPI_GRID_CLASS, "border-b border-border bg-muted py-2")} aria-hidden>
          <span />
          <span />
          <span className={KPI_HEADER_LABEL_CLASS}>{t("iam.orgSettings.kpis.column.name", "Display name")}</span>
          <span className={KPI_HEADER_LABEL_CLASS}>{t("iam.orgSettings.kpis.column.format", "Format")}</span>
          <span className={KPI_HEADER_LABEL_CLASS}>{t("iam.orgSettings.kpis.column.polarity", "Polarity")}</span>
          <span className={KPI_HEADER_LABEL_CLASS}>{t("iam.orgSettings.kpis.column.decimals", "Dec.")}</span>
          <span className={KPI_HEADER_LABEL_CLASS}>{t("iam.orgSettings.kpis.column.visible", "Visible")}</span>
          <span />
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <ul className="flex flex-col">
              {rows.map((row) => (
                <KpiRow
                  key={row.id}
                  row={row}
                  readOnly={readOnly}
                  canDelete={canManage && !locked}
                  canReorder={canManage && !locked}
                  onPatch={(body) => updateMut.mutate({ id: row.id, body })}
                  onReset={() => deleteMut.mutate(row.id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      </div>

      {/* Same modal the drawers use — `scope="org"` drops the per-user
          "Share with org" switch, since an Org KPI is already shared. */}
      <KpiSettingsDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        scope="org"
        isSubmitting={createMut.isPending}
        onSubmit={(value: KpiSettingsDialogValue) =>
          createMut.mutate({
            displayName: value.displayName,
            formula: value.formula,
            format: value.format,
            polarity: value.polarity,
            decimals: value.decimals,
            visible: true,
            colorToken: value.colorToken,
          })
        }
      />
    </div>
  );
}

function KpiRow({
  row,
  readOnly,
  canDelete,
  canReorder,
  onPatch,
  onReset,
}: {
  row: KpiCatalogEntry;
  readOnly: boolean;
  canDelete: boolean;
  canReorder: boolean;
  onPatch: (body: Parameters<typeof updateTenantKpi>[2]) => void;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const pinned = row.isPinned;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(row.id),
    disabled: pinned || !canReorder,
  });
  const note = kpiRowNote(t, row);

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex flex-col border-b border-border-subtle last:border-b-0",
        pinned ? "bg-muted" : "bg-background",
        isDragging && "relative z-10 shadow-md",
      )}
      data-testid={`org-settings-kpi-row-${row.name}`}
    >
      <div className={cn(KPI_GRID_CLASS, note ? "pb-1 pt-2" : "py-2")}>
        {pinned ? (
          <Lock className="size-3.5 text-muted-foreground/60" aria-hidden />
        ) : (
          <button
            type="button"
            className={cn(
              "flex size-5 items-center justify-center rounded text-muted-foreground/60",
              canReorder ? "cursor-grab hover:text-muted-foreground active:cursor-grabbing" : "cursor-not-allowed",
            )}
            aria-label={t("iam.orgSettings.kpis.reorder", "Reorder {{name}}", { name: row.displayName })}
            disabled={!canReorder}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="size-3.5" />
          </button>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={readOnly}
              aria-label={t("kpiSettings.color", "Color")}
              className="size-4 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:opacity-50"
              style={{ backgroundColor: resolveCatalogColorVar(row.name, row.index, row.colorToken) }}
            />
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto">
            <KpiColorPicker
              value={asKpiColorToken(row.name, row.index, row.colorToken)}
              disabled={readOnly}
              onChange={(colorToken: KpiColorToken) => onPatch({ colorToken })}
            />
          </PopoverContent>
        </Popover>
        <div className="relative min-w-0">
          <input
            className={cn(KPI_CONTROL_CLASS, "px-2.5 text-[13px]", row.isCustom && "pr-16")}
            defaultValue={row.displayName}
            key={`${row.id}-${row.displayName}`}
            disabled={readOnly}
            aria-label={t("iam.orgSettings.kpis.displayName", "Display name")}
            onBlur={(event) => {
              const next = event.target.value.trim();
              if (next && next !== row.displayName) onPatch({ displayName: next });
            }}
          />
          {row.isCustom && (
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded-sm bg-success-subtle px-1.5 py-[3px] text-[9px] font-semibold uppercase leading-none text-success-subtle-foreground">
              {t("iam.orgSettings.kpis.customBadge", "Custom")}
            </span>
          )}
        </div>
        <select
          className={cn(KPI_CONTROL_CLASS, "px-2")}
          value={row.format}
          disabled={readOnly}
          aria-label={t("iam.orgSettings.kpis.format", "Format")}
          onChange={(event) => onPatch({ format: event.target.value as KpiCatalogFormat })}
        >
          {FORMAT_OPTIONS.map((format) => (
            <option key={format} value={format}>
              {t(`iam.orgSettings.kpis.format.${format.toLowerCase()}`, format)}
            </option>
          ))}
        </select>
        <select
          className={cn(KPI_CONTROL_CLASS, "px-2")}
          value={row.polarity}
          disabled={readOnly}
          aria-label={t("iam.orgSettings.kpis.polarity", "Polarity")}
          onChange={(event) => onPatch({ polarity: event.target.value as KpiPolarity })}
        >
          {POLARITY_OPTIONS.map((polarity) => (
            <option key={polarity} value={polarity}>
              {polarity === "NEGATIVE"
                ? t("iam.orgSettings.kpis.polarityNegative", "Above zero is negative")
                : t("iam.orgSettings.kpis.polarityPositive", "Above zero is positive")}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={0}
          max={KPI_DECIMALS_MAX}
          className={cn(KPI_CONTROL_CLASS, "px-2")}
          defaultValue={row.decimals}
          key={`${row.id}-decimals-${row.decimals}`}
          disabled={readOnly}
          aria-label={t("iam.orgSettings.kpis.decimals", "Decimals")}
          onBlur={(event) => {
            const next = clampKpiDecimals(Number(event.target.value));
            if (next !== row.decimals) onPatch({ decimals: next });
          }}
        />
        {pinned ? (
          <span className="flex items-center gap-1 text-[11px] leading-none text-muted-foreground">
            <Lock className="size-3 text-muted-foreground/60" aria-hidden />
            {t("iam.orgSettings.kpis.alwaysVisible", "Always")}
          </span>
        ) : (
          <span className="flex items-center gap-1.5">
            <Switch
              checked={row.visible}
              disabled={readOnly}
              aria-label={t("iam.orgSettings.kpis.visible", "Visible")}
              onCheckedChange={(visible) => onPatch({ visible })}
            />
            {row.visible ? (
              <Eye className="size-3 text-muted-foreground" aria-hidden />
            ) : (
              <EyeOff className="size-3 text-muted-foreground" aria-hidden />
            )}
          </span>
        )}
        <KpiRowAction row={row} canDelete={canDelete} onReset={onReset} />
      </div>
      {note}
    </li>
  );
}

function KpiRowAction({
  row,
  canDelete,
  onReset,
}: {
  row: KpiCatalogEntry;
  canDelete: boolean;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  if (!canDelete) return <span />;
  if (row.isCustom) {
    return (
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="text-destructive hover:text-destructive"
        aria-label={t("iam.orgSettings.kpis.deleteNamed", "Delete {{name}}", { name: row.displayName })}
        onClick={onReset}
      >
        <Trash2 className="size-3.5" />
      </Button>
    );
  }
  if (row.isOverride) {
    return (
      <Button
        type="button"
        size="icon"
        variant="ghost"
        aria-label={t("iam.orgSettings.kpis.resetNamed", "Reset {{name}} to the catalog default", { name: row.displayName })}
        onClick={onReset}
      >
        <RotateCcw className="size-3.5" />
      </Button>
    );
  }
  return <span />;
}

function kpiRowNote(t: ReturnType<typeof useTranslation>["t"], row: KpiCatalogEntry) {
  if (row.isCustom && row.formula) {
    return (
      <p className={cn(KPI_NOTE_INDENT_CLASS, "truncate pb-2 font-mono text-[11px] leading-[1.45] text-muted-foreground")}>
        {row.formula}
      </p>
    );
  }
  if (row.isOverride && !row.isCustom) {
    return (
      <p className={cn(KPI_NOTE_INDENT_CLASS, "pb-2 text-[11px] leading-[1.45] text-warning-subtle-foreground")}>
        {row.displayName !== row.name
          ? t("iam.orgSettings.kpis.renamedNote", "Renamed from {{name}} · reset restores the catalog default", { name: row.name })
          : t("iam.orgSettings.kpis.overrideNote", "Edited for this org · reset restores the catalog default")}
      </p>
    );
  }
  return null;
}
